import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { ArrowLeft, Save, Edit, Trash2, Loader2, Truck, Printer } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import LiftingAdvicePrint from './LiftingAdvicePrint';

const Field = ({ label, children, required = false }) => (
  <div className="space-y-1">
    <Label className="text-xs font-semibold text-gray-600 font-ibm">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

const LiftingAdviceForm = () => {
  const { docketId } = useParams();
  const [searchParams] = useSearchParams();
  const docketIdParam = searchParams.get('docket_id');
  const finalDocketId = docketId || docketIdParam;
  
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [locations, setLocations] = useState([]);
  const [custodians, setCustodians] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showPrint, setShowPrint] = useState(false);
  const [savedAdvice, setSavedAdvice] = useState(null);
  const [company, setCompany] = useState(null);
  const [docket, setDocket] = useState(null);
  const [existingAdviceId, setExistingAdviceId] = useState(null);
  
  const [formData, setFormData] = useState({
    job_no: '',
    date: new Date().toISOString().split('T')[0],
    airways: '',
    leo_no: '',
    leo_date: '',
    to_location_id: '',
    to_location_text: '',
    to_custodian_id: '',
    to_custodian_address: '',
    employee_id: (() => { try { return JSON.parse(localStorage.getItem('user_profile'))?.id || ''; } catch { return ''; } })(),
    mawb_no: '',
    hawb_no: '',
    sb_no: '',
    sb_date: '',
    gsec_challan_no: '',
    no_of_pieces: '',
    gross_weight: '',
    contents: '',
    from_location_id: '',
    to_location_final_id: ''
  });
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = () => ({ 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' });
  
  useEffect(() => {
    loadLocations();
    loadCustodians();
    loadCompany();
    if (finalDocketId) {
      loadPrefillData();
    } else {
      setLoading(false);
    }
  }, [finalDocketId]);

  // Load employees of the docket's owning company (or current user's company
  // for non-super-admin contexts). Runs after `company` is set so we know the
  // correct company_id.
  useEffect(() => {
    const loadEmployees = async () => {
      try {
        const cid = company?.id || localStorage.getItem('company_id');
        if (!cid) return;
        const res = await fetch(`${backendUrl}/api/users?company_id=${cid}&limit=500`, { headers: getAuth() });
        if (res.ok) {
          const emps = await res.json();
          setEmployees(emps);
          setFormData(prev => {
            if (prev.employee_id) return prev;
            try {
              const currentUserId = JSON.parse(localStorage.getItem('user_profile'))?.id;
              if (currentUserId && emps.some(e => e.id === currentUserId)) {
                return { ...prev, employee_id: currentUserId };
              }
            } catch {}
            return prev;
          });
        }
      } catch (e) { console.error('Failed to load employees:', e); }
    };
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const loadCustodians = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/custodians?is_active=true`, { headers: getAuth() });
      if (res.ok) setCustodians(await res.json());
    } catch (e) { console.error('Failed to load custodians:', e); }
  };

  const loadCompany = async () => {
    try {
      // Prefer the docket's owning company (so super-admins viewing a docket
      // from another company still see that company's logo/branding).
      let companyId = null;
      if (finalDocketId) {
        try {
          const dRes = await fetch(`${backendUrl}/api/dockets/${finalDocketId}`, { headers: getAuth() });
          if (dRes.ok) {
            const d = await dRes.json();
            companyId = d.company_id || null;
          }
        } catch (e) { /* fall through to localStorage */ }
      }
      if (!companyId) companyId = localStorage.getItem('company_id');
      if (!companyId) return;
      const res = await fetch(`${backendUrl}/api/companies/${companyId}`, { headers: getAuth() });
      if (res.ok) setCompany(await res.json());
    } catch (e) {
      console.error('Failed to load company:', e);
    }
  };
  
  const loadLocations = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/locations`, { headers: getAuth() });
      if (res.ok) {
        const data = await res.json();
        setLocations(data);
      }
    } catch (e) {
      console.error('Failed to load locations:', e);
    }
  };
  
  const loadPrefillData = async () => {
    try {
      setLoading(true);

      const loggedInUserId = (() => {
        try { return JSON.parse(localStorage.getItem('user_profile'))?.id || ''; } catch { return ''; }
      })();

      // 1. Fetch prefill data from dedicated backend prefill endpoint
      let prefill = {};
      try {
        const pRes = await fetch(`${backendUrl}/api/lifting-advices/prefill-data/${finalDocketId}`, { headers: getAuth() });
        if (pRes.ok) prefill = await pRes.json();
      } catch (err) {
        console.warn('Prefill endpoint error:', err);
      }

      // 2. Check if a lifting advice already exists for this docket
      const existingRes = await fetch(`${backendUrl}/api/lifting-advices?docket_id=${finalDocketId}`, { headers: getAuth() });
      if (existingRes.ok) {
        const existing = await existingRes.json();
        if (existing.length > 0) {
          const rec = existing[0];
          setExistingAdviceId(rec.id);
          setSavedAdvice(rec);
          setFormData({
            job_no: rec.job_no || prefill.job_no || '',
            date: rec.date || prefill.date || new Date().toISOString().split('T')[0],
            airways: rec.airways || prefill.airways || '',
            leo_no: rec.leo_no || '',
            leo_date: rec.leo_date || '',
            to_location_id: rec.to_location_id || '',
            to_location_text: rec.to_location_text || '',
            to_custodian_id: rec.to_custodian_id || '',
            to_custodian_address: rec.to_custodian_address || '',
            employee_id: rec.employee_id || loggedInUserId || prefill.employee_id || '',
            mawb_no: rec.mawb_no || prefill.mawb_no || '',
            hawb_no: rec.hawb_no || prefill.hawb_no || '',
            sb_no: rec.sb_no || prefill.sb_no || '',
            sb_date: rec.sb_date || prefill.sb_date || '',
            gsec_challan_no: rec.gsec_challan_no || '',
            no_of_pieces: rec.no_of_pieces || prefill.no_of_pieces || '',
            gross_weight: rec.gross_weight || prefill.gross_weight || '',
            contents: rec.contents || prefill.contents || '',
            from_location_id: rec.from_location_id || prefill.from_location_id || '',
            to_location_final_id: rec.to_location_final_id || prefill.to_location_final_id || '',
          });
          return;
        }
      }

      // 3. New advice: prefill from prefill API with fallback to direct docket
      let docket = null;
      let awbData = {};
      let shipmentInfo = {};
      let packageInfo = {};
      let invoices = [];

      try {
        const docketRes = await fetch(`${backendUrl}/api/dockets/${finalDocketId}`, { headers: getAuth() });
        if (docketRes.ok) {
          docket = await docketRes.json();
          setDocket(docket);
          const payload = docket.payload_json || {};
          packageInfo = payload.package_info || {};
          invoices = payload.invoices || [];
          shipmentInfo = payload.shipment_info || {};
        }
        const awbRes = await fetch(`${backendUrl}/api/dockets/${finalDocketId}/documents/AIR_WAYBILL`, { headers: getAuth() });
        if (awbRes.ok) {
          const awbDoc = await awbRes.json();
          awbData = awbDoc?.document_data || {};
        }
      } catch (e) {
        console.warn('Fallback docket fetch:', e);
      }

      setFormData(prev => ({
        ...prev,
        job_no: prefill.job_no || docket?.system_docket_no || '',
        date: prefill.date || new Date().toISOString().split('T')[0],
        airways: prefill.airways || awbData.requested_flight || shipmentInfo.flight_no_1 || '',
        employee_id: loggedInUserId || prefill.employee_id || prev.employee_id || '',
        mawb_no: prefill.mawb_no || awbData.master_awb_no || shipmentInfo.mawb_no || '',
        hawb_no: prefill.hawb_no || awbData.hawb_no || shipmentInfo.hawb_no || '',
        sb_no: prefill.sb_no || '',
        sb_date: prefill.sb_date || '',
        no_of_pieces: prefill.no_of_pieces || packageInfo.package_no_of_packages?.toString() || '',
        gross_weight: prefill.gross_weight || packageInfo.package_gross_weight?.toString() || '',
        contents: prefill.contents || (invoices.length > 0 ? invoices[0].description : ''),
        from_location_id: prefill.from_location_id || shipmentInfo.loading_from || shipmentInfo.place_of_receipts || '',
        to_location_final_id: prefill.to_location_final_id || shipmentInfo.discharge_at || shipmentInfo.delivery_at || shipmentInfo.clearance_at_destination || '',
      }));
    } catch (e) {
      console.error('Failed to load prefill data:', e);
    } finally {
      setLoading(false);
    }
  };
  
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSave = async () => {
    // Validation
    if (!formData.job_no || !formData.date) {
      toast({ title: 'Validation Error', description: 'Job No and Date are required', variant: 'destructive' });
      return;
    }
    if (formData.no_of_pieces && parseFloat(formData.no_of_pieces) <= 0) {
      toast({ title: 'Validation Error', description: 'No of pieces must be greater than 0', variant: 'destructive' });
      return;
    }
    
    if (formData.gross_weight && parseFloat(formData.gross_weight) <= 0) {
      toast({ title: 'Validation Error', description: 'Gross weight must be greater than 0', variant: 'destructive' });
      return;
    }
    
    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        docket_id: finalDocketId,
        // Convert empty strings to null for optional date/FK fields
        leo_date: formData.leo_date || null,
        sb_date: formData.sb_date || null,
        to_location_id: formData.to_location_id || null,
        from_location_id: formData.from_location_id || null,
        to_location_final_id: formData.to_location_final_id || null,
      };

      const url = existingAdviceId
        ? `${backendUrl}/api/lifting-advices/${existingAdviceId}`
        : `${backendUrl}/api/lifting-advices`;
      const method = existingAdviceId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: getAuth(),
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const error = await res.json();
        const detail = Array.isArray(error.detail)
          ? error.detail.map(e => e.msg).join(', ')
          : error.detail || 'Failed to save lifting advice';
        throw new Error(detail);
      }
      
      const savedData = await res.json();
      toast({ title: 'Success', description: 'Lifting advice saved successfully' });
      if (!existingAdviceId) setExistingAdviceId(savedData.id);
      setSavedAdvice(savedData);
      setShowPrint(true);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-rose-600" />
      </div>
    );
  }

  // Print overlay — shown after successful save
  if (showPrint && savedAdvice) {
    return (
      <div className="min-h-screen bg-gray-100 print:bg-white print:min-h-0">
        {/* Controls bar — sticky inside the content area (matches DocketPrint pattern) */}
        <div
          id="print-controls"
          className="print:hidden bg-white border-b sticky top-0 z-10 shadow-sm"
        >
          <div className="max-w-[900px] mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
              data-testid="back-from-print-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <span className="text-xs text-gray-500 hidden md:inline">
              5-page print pack — Lifting Advice, Security Declarations, General Declaration &amp; MAWB Statement
            </span>
            <Button
              onClick={() => window.print()}
              className="bg-rose-600 hover:bg-rose-700 flex items-center gap-2"
              data-testid="print-all-pages-btn"
            >
              <Printer className="w-4 h-4" />
              Print All Pages
            </Button>
          </div>
        </div>

        {/* Print content */}
        <LiftingAdvicePrint
          adviceData={savedAdvice}
          companyData={company}
          docketData={docket || { id: finalDocketId }}
        />

        {/* Hide controls bar when printing */}
        <style>{`
          @media print {
            #print-controls {
              display: none !important;
            }
          }
        `}</style>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-7xl mx-auto space-y-5">
        
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
                <Truck className="w-5 h-5 text-rose-600" />
                {existingAdviceId ? 'Edit Lifting Advice' : 'Add Lifting Advice'}
              </h1>
              <p className="text-sm text-text-secondary font-ibm">
                Job No: {formData.job_no || 'N/A'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {existingAdviceId && (
              <Button
                variant="outline"
                onClick={() => setShowPrint(true)}
                className="flex items-center gap-2 border-rose-600 text-rose-600 hover:bg-rose-50"
                data-testid="print-btn"
              >
                <Printer className="w-4 h-4" />
                Print
              </Button>
            )}
            <Button onClick={handleSave} disabled={saving} className="bg-rose-600 hover:bg-rose-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>
        
        {/* Form Card */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-rose-600 to-rose-500 px-5 py-3">
            <h2 className="text-base font-semibold text-white font-ibm">Lifting Advice Details</h2>
          </div>
          <div className="p-5 space-y-4">
            
            {/* Row 1 */}
            <div className="grid grid-cols-5 gap-4">
              <Field label="Job No" required>
                <Input value={formData.job_no} onChange={e => handleChange('job_no', e.target.value)} placeholder="Job No" />
              </Field>
              <Field label="Date" required>
                <Input type="date" value={formData.date} onChange={e => handleChange('date', e.target.value)} />
              </Field>
              <Field label="Airways">
                <Input value={formData.airways} onChange={e => handleChange('airways', e.target.value)} placeholder="Airways" />
              </Field>
              <Field label="LEO No">
                <Input value={formData.leo_no} onChange={e => handleChange('leo_no', e.target.value)} placeholder="LEO No" />
              </Field>
              <Field label="LEO Date">
                <Input type="date" value={formData.leo_date} onChange={e => handleChange('leo_date', e.target.value)} />
              </Field>
            </div>
            
            {/* Row 2 */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="To">
                <select
                  value={formData.to_custodian_id}
                  onChange={(e) => {
                    const cid = e.target.value;
                    const c = custodians.find(x => x.id === cid);
                    setFormData(prev => ({
                      ...prev,
                      to_custodian_id: cid,
                      to_location_text: c ? c.name : prev.to_location_text,
                      to_custodian_address: c ? c.address : '',
                    }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 bg-white"
                  data-testid="lifting-advice-to-custodian-select"
                >
                  <option value="">— Select Custodian —</option>
                  {custodians.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {formData.to_custodian_address && (
                  <p className="mt-1 text-xs text-gray-500 whitespace-pre-line">{formData.to_custodian_address}</p>
                )}
              </Field>
              <Field label="Select Employee">
                <select
                  value={formData.employee_id}
                  onChange={(e) => handleChange('employee_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 bg-white"
                  data-testid="lifting-advice-employee-select"
                >
                  <option value="">— Select Employee —</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.full_name || emp.name || emp.email}{emp.employee_id ? ` (${emp.employee_id})` : ''}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            
            {/* Row 3 */}
            <div className="grid grid-cols-5 gap-4">
              <Field label="MAWB No">
                <Input value={formData.mawb_no} onChange={e => handleChange('mawb_no', e.target.value)} placeholder="MAWB No" />
              </Field>
              <Field label="HAWB No">
                <Input value={formData.hawb_no} onChange={e => handleChange('hawb_no', e.target.value)} placeholder="HAWB No" />
              </Field>
              <Field label="S/B No">
                <Input value={formData.sb_no} onChange={e => handleChange('sb_no', e.target.value)} placeholder="S/B No" />
              </Field>
              <Field label="S/B Date">
                <Input type="date" value={formData.sb_date} onChange={e => handleChange('sb_date', e.target.value)} />
              </Field>
              <Field label="GSEC CHALLAN No">
                <Input value={formData.gsec_challan_no} onChange={e => handleChange('gsec_challan_no', e.target.value)} placeholder="GSEC CHALLAN No" />
              </Field>
            </div>
            
            {/* Row 4 */}
            <div className="grid grid-cols-3 gap-4">
              <Field label="No. of Pieces">
                <Input type="number" value={formData.no_of_pieces} onChange={e => handleChange('no_of_pieces', e.target.value)} placeholder="No. of Pieces" />
              </Field>
              <Field label="Gross Weight">
                <Input type="number" step="0.01" value={formData.gross_weight} onChange={e => handleChange('gross_weight', e.target.value)} placeholder="Gross Weight" />
              </Field>
              <Field label="Contents">
                <Input value={formData.contents} onChange={e => handleChange('contents', e.target.value)} placeholder="Contents" />
              </Field>
            </div>
            
            {/* Row 5 */}
            <div className="grid grid-cols-2 gap-4">
              <Field label="From" required>
                <select
                  value={formData.from_location_id}
                  onChange={e => handleChange('from_location_id', e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
                >
                  <option value="">-- Select --</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.location_name}</option>
                  ))}
                </select>
              </Field>
              <Field label="To" required>
                <select
                  value={formData.to_location_final_id}
                  onChange={e => handleChange('to_location_final_id', e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors"
                >
                  <option value="">-- Select --</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.location_name}</option>
                  ))}
                </select>
              </Field>
            </div>
            
          </div>
        </Card>
        
      </div>
    </div>
  );
};

export default LiftingAdviceForm;
